import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import styles from "./Portfolios.module.css";
import cn from 'classnames';
import SidebarLayout from '../../../../../component/common/Layout/SidebarLayout/SidebarLayout';
import TextHeader from '../../../../../component/common/TextHeader/TextHeader';
import ProAccountsCard, { IDifferenceType } from '../../../../../component/common/ProAccountsCard/ProAccountsCard';
import UserBanner from '../../../../../component/UI/UserBanner/UserBanner';
import ContentTabs from '../../../../../component/common/ContentTabs/ContentTabs';
import CatchRatePerformanceCard from '../../../../../component/UI/CatchRatePerformanceCard/CatchRatePerformanceCard';
import AnglersSettingsTable from './component/AnglersSettingsTable/AnglersSettingsTable';
import ReferralsTable from './component/ReferralsTable/ReferralsTable';
import { useLocation, useNavigate } from 'react-router-dom';
import BlockedUsersTable from './component/BlockedUsersTable/BlockedUsersTable';
import RecentCatchGallery from './component/RecentCatchGallery/RecentCatchGallery';
import UserBannerSkeleton from '../../../../../component/UI/UserBanner/UserBannerSkeleton';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../redux/store';
import { fetchUserPortfolio, fetchUserCatchMetrics, portfolioSearch } from '../../../../../redux/slices/portfolio/actions';
import { clearPortfolioSearch } from '../../../../../redux/slices/portfolio/slice';
import ProAccountsCardSkeleton from '../../../../../component/common/ProAccountsCard/ProAccountsCardSkeleton';
import CatchRatePerformanceCardSkeleton from '../../../../../component/UI/CatchRatePerformanceCard/CatchRatePerformanceCardSkeleton';
import { capitalizedUserName } from '../../../../../utils';
import FilterSelect from '../../../../../component/common/FilterSelect/FilterSelect';
import { useDebounce } from '../../../../../hooks/useDebounce';

const Portfolios: FunctionComponent = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        return () => {
            dispatch(clearPortfolioSearch());
            setSearchQuery('');
        }
    }, [])
    
    const [activeTab, setActiveTab] = useState('Statistics');
    const [activeAnglersTab, setActiveAnglersTab] = useState('Login Activity');

    const handleTabClick = useCallback((tab: string) => {
        setActiveTab(tab);
    }, []);

    const handleAnglersTabClick = useCallback((tab: string) => {
        setActiveAnglersTab(tab);
    }, []);

    const location = useLocation();
    const { userId: userIdFromParams } = useParams<{ userId: string }>();
    const { user: loggedInUser } = useSelector((state: RootState) => state.auth);
    
    const userId = userIdFromParams ?? loggedInUser?.id;    
    const dispatch = useDispatch<AppDispatch>();
    const { portfolio, loading, catchMetrics, catchMetricsLoading, referralListLoading, portfolioSearch: searchedUsers, portfolioSearchLoading } = useSelector((state: RootState) => state.portfolio);
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    useEffect(() => {
        if (debouncedSearchQuery) {
            dispatch(portfolioSearch(debouncedSearchQuery));
        }
    }, [debouncedSearchQuery, dispatch]);

    const userOptions = useMemo(() => {
        if (!searchedUsers) return [];
        return searchedUsers.map(user => ({ value: user.id, label: user.name }));
    }, [searchedUsers]);

    const handleUserSelect = (userId: string) => {
        if (userId) {
            navigate(`/pro-accounts/portfolios/${userId}`);
        }
    };

    useEffect(() => {
        if (userId) {
            dispatch(fetchUserPortfolio(userId as string));
            dispatch(fetchUserCatchMetrics(userId as string));
        }
    }, [dispatch, userId]);

    useEffect(() => {
        if (location.hash && referralListLoading !== 'pending') {
            const el = document.getElementById(location.hash.replace("#", ""));
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }, [location, referralListLoading]);

    const getDifferenceType = (value: number) => value >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE;

    const overView = useMemo(() => catchMetrics ? [
        {
            title: 'Total Catches',
            value: `${catchMetrics.totalCatches}`,
            differenceType: getDifferenceType(Number(catchMetrics.currentMonthCatches)),
            differenceText: `${Number(catchMetrics.currentMonthCatches)} this month`,
            actualDifference: Number(catchMetrics.currentMonthCatches),
        },
        {
            title: 'Total Weight',
            value: `${catchMetrics.totalWeight} ${catchMetrics.unit}`,
            differenceType: getDifferenceType(catchMetrics.currentMonthWeight),
            differenceText: `${catchMetrics.currentMonthWeight} ${catchMetrics.unit} this month`,
            actualDifference: catchMetrics.currentMonthWeight,
        },
        {
            title: 'Average Weight',
            value: `${catchMetrics.avgWeight} ${catchMetrics.unit}`,
            differenceType: getDifferenceType(catchMetrics.currentMonthAvgWeight),
            differenceText: `${catchMetrics.currentMonthAvgWeight} ${catchMetrics.unit} from last month`,
            actualDifference: catchMetrics.currentMonthAvgWeight,
        },
        {
            title: 'Best Catch',
            value: `${catchMetrics.bestCatchWeight} ${catchMetrics.unit}`,
            differenceType: IDifferenceType.INCREASE,
            differenceText: 'Personal record',
            actualDifference: 0,
        },
    ] : [], [catchMetrics]);

    return (
        <SidebarLayout>
            <div  className={styles.portfolioPage}>
                <div className={cn(styles.proAccountsTopRow)}>
                    <div className={cn(styles.portfolioHeader, 'mb-4')}>
                        <div className={styles.portfolioTextHeader}>
                        <TextHeader 
                            title={userId == loggedInUser?.id ? 'My Portfolio' : 'User Portfolio'} 
                            summary={portfolio ? `${capitalizedUserName(portfolio.user_full_name) || 'User'}'s fishing profile and statistics` : ''}
                            className='mb-0'
                        />
                        </div>
                        <div className={styles.portfolioDropdown}>
                        <FilterSelect
                            options={userOptions}
                            onSelect={(value) => handleUserSelect(value as string)}
                            placeholder={'Select User'} 
                            isSearchable= {true}
                            onInputChange={setSearchQuery}
                            isLoading={portfolioSearchLoading === 'pending'}
                        />
                        </div>

                    </div>
                    
                    {loading === 'pending' ? (
                        <UserBannerSkeleton />
                    ) : (
                        <UserBanner portfolio={portfolio} />
                    )}
                </div>
            
                <div className={cn(styles.proAccountsRowTwo)}>
                    <ContentTabs tabs={['Statistics', 'Recent Catches']} 
                        defaultActiveTab={activeTab} 
                        tabOnClick={handleTabClick} 
                        className='mb-4'
                    />
                    {activeTab === 'Statistics' ? 
                    <>
                        <div className={styles.portfolioCardsRow}>
                            {catchMetricsLoading === 'pending' ? (                     
                                Array.from({length: 4}).map((_, index)=> (
                                    <ProAccountsCardSkeleton key={index} className={styles.portfolioOverViewCard} />
                                ))
                            ) :
                            overView.map((item, index) => (
                                <ProAccountsCard 
                                    key={`${item.title}-${index}`}
                                    className={styles.portfolioOverViewCard} 
                                    title={item.title} 
                                    value={item.value}
                                    differenceType={item.differenceType} 
                                    differenceText={item.differenceText} 
                                    actualDifference={item.actualDifference}
                                />
                            ))}                       
                        </div>
                        {catchMetricsLoading === 'pending' ? <CatchRatePerformanceCardSkeleton /> : catchMetrics && <CatchRatePerformanceCard 
                            successRate={catchMetrics.successRate}
                            totalTrips={catchMetrics.totalTrips}
                            successfulTrips={catchMetrics.successfultrips}
                            unsuccessfulTrips={catchMetrics.unsuccessTrips}
                            tournamentWins={catchMetrics.tournamentWins}
                        />}                    
                    </>
                    :
                        (userId && <RecentCatchGallery userId= {userId}/>)
                    }
                </div>
                <div className={cn(styles.proAccountsRowThree)} id="anglers-settings">
                    <TextHeader 
                        title={'Anglers’ Settings'} 
                        className='mb-1'
                    />
                    <ContentTabs tabs={['Login Activity', 'Blocked Users']} 
                        defaultActiveTab={activeAnglersTab} 
                        tabOnClick={handleAnglersTabClick} 
                        className='mb-4'
                    />
                    {activeAnglersTab === 'Login Activity' ? 
                    (userId && <AnglersSettingsTable userId={ userId } />)
                    : 
                    (userId && <BlockedUsersTable userId= { userId }/>)
                    }
                </div>
                <div id='referrals' className={cn(styles.proAccountsRowFour)}>
                    <TextHeader 
                        title={'Referrals'} 
                        className='mb-2'
                    />
                    {userId && <ReferralsTable userId= {userId}/>}
                </div>
            </div>
        </SidebarLayout>
    );
};

export default Portfolios;